import { EMPTY_CHART_CONFIG } from "@common/controls/Chart/lib";
import { type ChartConfig } from "@common/controls/Chart/types";
import { DelayPreloader } from "@common/controls/Preloader/component";
import { type GenericMap } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { parseQueryString } from "@common/lib/url";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import { isUndefined } from "lodash";
import React from "react";
import { collapseReportParameters, getReportBadges } from "../lib";
import { type BadgeInfo, type DrillDownInfo, type GridConfig, type ProviderData, type ReportBreadcrumb } from "../types";
import { type ProviderProps } from "./types";

export const NETWORK_GENEALOGY_DOWNLINE_REPORT_ID = "211812DE-F110-4AB1-87E6-EB2B06C56CD4";
export const NETWORK_GENEALOGY_UPLINE_REPORT_ID = "9255AAF4-383D-448E-A33A-2DFE36CE8876";

type Ids = {
  id: string;
  rootId: string | null;
};
type ReportBackendProviderProps = ProviderProps & {
  get?: (id: string) => Promise<Data.Api.Report>;
};
const ReportBackendProvider = (props: ReportBackendProviderProps) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const [ids, setIds] = React.useState<Ids | string>(props.id);
  const [data, setData] = React.useState<ProviderData | null | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(true);
  const [drillDownInfo, setDrillDownInfo] = React.useState<DrillDownInfo[]>([]);
  const [parameters, setParameters] = React.useState(collapseReportParameters(parseQueryString()));

  React.useEffect(() => {
    setIsLoading(true);
    setIds(props.id);
    setDrillDownInfo([]);
  }, [props.id]);

  React.useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const { id, rootId }: Ids = typeof ids === "string" ? { id: ids, rootId: null } : ids;
        const [report, fields] = await Promise.all([
          (props.get ?? api.get)(id),
          props.fields ? props.fields : api.getFormFields(rootId ?? id)
        ]);

        if (!isCancelled) {
          setData({ report, fields });
        }
      } finally {
        !isCancelled && setIsLoading(false);
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [props.get, ids, setData, setIsLoading, props.fields]);

  React.useEffect(() => {
    !isUndefined(data) && props.onLoaded && props.onLoaded();
  }, [data]);

  const config = React.useMemo((): ChartConfig => {
    if (!data?.report || !data.report.chartSettingsJson) return EMPTY_CHART_CONFIG;
    try {
      return JSON.parse(data.report.chartSettingsJson) as ChartConfig;
    } catch {
      return EMPTY_CHART_CONFIG;
    }
  }, [data?.report]);

  const gridConfig = React.useMemo((): GridConfig | null | undefined => {
    if (!data) return;

    if (data.report.gridSettingsJson === "") return null;
    try {
      return JSON.parse(data.report.gridSettingsJson) as GridConfig;
    } catch {
      return;
    }
  }, [data?.report]);

  const onDrillDown = React.useCallback(
    (value: any, label: string, parameters: GenericMap<any>) => {
      if (!data?.report.childReportId || !config.drillDownKey) throw new Error("BUG");

      setDrillDownInfo([
        ...drillDownInfo,
        {
          key: config.drillDownKey,
          value,
          reportId: data.report.reportId,
          reportName: data.report.name,
          label: i18n[config.drillDownLabel ?? config.drillDownKey],
          valueLabel: label
        }
      ]);
      setIds({
        id: data.report.childReportId,
        rootId: typeof ids === "object" ? ids.rootId : ids
      });
      setParameters(parameters);
    },
    [
      i18n,
      drillDownInfo,
      config.drillDownKey,
      data?.report.childReportId,
      data?.report.name,
      data?.report.reportId,
      setDrillDownInfo,
      setIds
    ]
  );

  const goTo = React.useCallback(
    (reportId: string) => {
      if (!drillDownInfo.length) throw new Error("BUG");

      const index = drillDownInfo.findIndex((x) => x.reportId === reportId);
      if (index === -1) throw new Error("BUG");

      const ddi = drillDownInfo[index];
      setIds({
        id: ddi.reportId,
        rootId: typeof ids === "object" ? ids.rootId : ids
      });
      setDrillDownInfo(drillDownInfo.slice(0, index));
    },
    [drillDownInfo, setDrillDownInfo, setIds]
  );

  const breadcrumbs = React.useMemo<ReportBreadcrumb[]>(
    () =>
      drillDownInfo.map((x) => ({
        reportId: x.reportId,
        reportName: x.reportName
      })),
    [drillDownInfo]
  );

  const getBadges = React.useCallback(
    (parameters: GenericMap<any>, filters: Data.Api.Filter[], onBadgeClick: () => void): BadgeInfo[] => {
      if (!data) return [];

      return getReportBadges(i18n, {
        badges: data.report.badges ?? [],
        fields: data.fields,
        parameters: parameters,
        drillDownInfo: drillDownInfo,
        filters: filters,
        onBadgeClick: onBadgeClick
      });
    },
    [i18n, data, data?.fields, drillDownInfo, data?.report.chartStatement]
  );

  if (!data) return <DelayPreloader />;

  const { id }: Ids = typeof ids === "string" ? { id: ids, rootId: null } : ids;

  return props.children({
    id,
    breadcrumbs,
    config,
    drillDownInfo,
    fields: data!.fields,
    hasGrid: !!data!.report.hasGrid,
    goTo,
    gridConfig,
    isLoading,
    name: data.report.name,
    onDrillDown: data!.report.childReportId ? onDrillDown : undefined,
    parameters,
    type: data!.report.chartTypeKey,

    getBadges,
    getChartData: api.getChartData,
    getFilters: api.getFilters,
    getGridData: api.getGridData
  });
};

export default ReportBackendProvider;
